import { css } from "@emotion/css";
import {
  Avatar,
  Col,
  Flex,
  Row,
  Table,
  Typography,
  Input,
  message,
  Button,
} from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MinusOutlined } from "@ant-design/icons";
import { empAllTeamsAction } from "../../../../../../../../store/action/admin/employeesAction";
import {
  assignUsersToDimension,
  getUsersByDimension,
  removeUserFromDimension,
} from "../../../../../../../../store/action/admin/dimensionsEntityActions";
import CustomLoaderIcon from "../../../../../../../../Theme/CustomIcons/Icons/Essetional/CustomLoaderIcon";

const { Text } = Typography;

const AssignEmployeesTab = React.forwardRef((props, ref) => {
  const dispatch = useDispatch();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchText, setSearchText] = useState("");

  const themeDefault = useSelector((state) => state.themeDefault);
  const primaryColor = themeDefault?.info?.colorPrimary;
  const isDefaultTheme = themeDefault?.info?.themeColor === "default";

  const empAllTeamsList = useSelector((state) => state.empAllTeamsList);
  const { allEmpTeamsInfo } = empAllTeamsList;
  const allEmployees = allEmpTeamsInfo?.getExtraInfo || [];
  const getSingleDimensionList = useSelector(
    (state) => state.getSingleDimensionList
  );
  const getAllUsersToDimensionList = useSelector(
    (state) => state.getAllUsersToDimensionList
  );
  const dimensionId = getSingleDimensionList?.info?.dimension?._id;
  const isLoading = getAllUsersToDimensionList?.loading;
  useEffect(() => {
    if (!allEmpTeamsInfo) {
      dispatch(empAllTeamsAction());
    }
  }, [allEmpTeamsInfo, dispatch]);

  useEffect(() => {
    setSelectedRowKeys([]);
    setSearchText("");
  }, [dimensionId]);

  useEffect(() => {
    if (dimensionId) {
      dispatch(getUsersByDimension(dimensionId));
    }
  }, [dimensionId, dispatch]);

  useEffect(() => {
    const assignedUsers = getAllUsersToDimensionList?.info?.assignments || [];
    if (allEmployees.length === 0) return;

    const assignedUserIds = assignedUsers
      .map((user) => user.userId?._id)
      .filter(Boolean);

    const availableEmployeeIds = allEmployees
      .map((emp) => emp.userId?._id)
      .filter((id) => id && id !== "-");

    const validUserIds = assignedUserIds.filter((id) =>
      availableEmployeeIds.includes(id)
    );

    // Replace with current dimension's assignments (avoid merging with previous dimension)
    setSelectedRowKeys(validUserIds);
  }, [getAllUsersToDimensionList?.info, allEmployees]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const handleRemoveEmployee = async (userId) => {
    if (!dimensionId || !userId) return;

    try {
      await dispatch(removeUserFromDimension(userId, dimensionId));
      message.success("Employee removed from dimension successfully");
      await dispatch(getUsersByDimension(dimensionId));

      setSelectedRowKeys((prevKeys) =>
        prevKeys.filter((key) => key !== userId)
      );
    } catch (err) {
      message.error(err?.message || "Failed to remove employee from dimension");
    }
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const employeesData = allEmployees.map((item) => {
    const firstName = item.userId?.name?.firstName || "";
    const lastName = item.userId?.name?.lastName || "";
    const fullName = `${firstName} ${lastName}`.trim() || "-";
    const profileImageUrl = item.userId?.profileImage?.url || "";
    return {
      userId: item.userId?._id || "-",
      empId: item.empId || item.userId?.empId || "-",
      firstName,
      lastName,
      fullName,
      profileImageUrl,
      designation: item.designationId || "-",
      department: item.title && item.title.trim() !== "" ? item.title : "-",
    };
  });

  const columns = [
    {
      title: "Emp ID",
      align: "left",
      dataIndex: "empId",
      key: "empId",
      width: 100,
    },
    {
      title: "User Name",
      dataIndex: "fullName",
      align: "left",
      key: "fullName",
      render: (text, record) => {
        const initials = `${(record.firstName || "-").toString().charAt(0)}${(
          record.lastName || "-"
        )
          .toString()
          .charAt(0)}`.toUpperCase();

        return (
          <Flex align="center" gap={8}>
            <Avatar
              size={32}
              src={record.profileImageUrl || undefined}
              style={{ fontSize: 12, fontWeight: 500 }}
            >
              {initials}
            </Avatar>
            <span>{record.fullName}</span>
          </Flex>
        );
      },
    },
  ];

  const filteredEmployeesData = React.useMemo(() => {
    if (!searchText.trim()) {
      return employeesData;
    }
    const searchLower = searchText.toLowerCase().trim();
    return employeesData.filter((emp) => {
      const empIdMatch = String(emp.empId || "")
        .toLowerCase()
        .includes(searchLower);
      const fullNameMatch = (emp.fullName || "")
        .toLowerCase()
        .includes(searchLower);
      const departmentMatch = (emp.department || "")
        .toLowerCase()
        .includes(searchLower);
      return empIdMatch || fullNameMatch || departmentMatch;
    });
  }, [employeesData, searchText]);

  const selectedEmployees = React.useMemo(() => {
    return employeesData.filter((emp) => selectedRowKeys.includes(emp.userId));
  }, [employeesData, selectedRowKeys]);

  const allFilteredUserIds = React.useMemo(() => {
    return filteredEmployeesData
      .map((emp) => emp.userId)
      .filter((id) => id && id !== "-");
  }, [filteredEmployeesData]);

  const isAllSelected = React.useMemo(() => {
    if (allFilteredUserIds.length === 0) return false;
    return allFilteredUserIds.every((id) => selectedRowKeys.includes(id));
  }, [allFilteredUserIds, selectedRowKeys]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      setSelectedRowKeys([]);
    } else {
      setSelectedRowKeys(allFilteredUserIds);
    }
  };

  React.useImperativeHandle(ref, () => ({
    submit: async () => {
      const userIds = selectedRowKeys;
      try {
        if (dimensionId && userIds?.length > 0) {
          await dispatch(assignUsersToDimension(dimensionId, userIds));
          message.success("Employees assigned to dimension successfully");
          await dispatch(getUsersByDimension(dimensionId));
        }
      } catch (err) {
        const errorMsg =
          err?.response?.data?.message ||
          "One or more users are not enrolled or blocked";

        message.error(errorMsg);
        throw err;
      }

      return {
        selectedEmployees,
        selectedRowKeys,
        totalSelected: selectedEmployees.length,
      };
    },
  }));

  if (isLoading) {
    return (
      <Flex align="center" justify="center" style={{ minHeight: 400 }}>
        <CustomLoaderIcon />
      </Flex>
    );
  }

  return (
    <Row gutter={[16, 16]}>
      {/* LEFT PANEL - Selected Employees */}
      <Col span={8}>
        <Flex vertical gap={8}>
          <Text strong style={{ fontSize: 14 }}>
            Selected Employees ({selectedEmployees.length})
          </Text>

          {selectedEmployees.length === 0 ? (
            <Text type="secondary" style={{ fontSize: 12 }}>
              No employees selected
            </Text>
          ) : (
            selectedEmployees.map((item) => (
              <Flex
                key={item.userId}
                align="center"
                gap={10}
                className={css`
                  background: transparent;
                  padding: 6px 0;
                `}
              >
                {/* Remove Button */}
                <div
                  onClick={() => handleRemoveEmployee(item.userId)}
                  style={{
                    borderRadius: "50%",
                    minWidth: 24,
                    width: 24,
                    height: 24,
                    backgroundColor: primaryColor,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <MinusOutlined
                    style={{
                      color: isDefaultTheme ? "#ffffff" : "#000000",
                      fontSize: 12,
                    }}
                  />
                </div>

                {/* Avatar */}
                <Avatar
                  src={item.profileImageUrl || undefined}
                  style={{
                    backgroundColor: primaryColor,
                    fontSize: 13,
                    flexShrink: 0,
                  }}
                >
                  {`${(item.firstName || "-").toString().charAt(0)}${(
                    item.lastName || "-"
                  )
                    .toString()
                    .charAt(0)}`.toUpperCase()}
                </Avatar>

                {/* Employee Info */}
                <Flex vertical>
                  <Text strong style={{ fontSize: 13 }}>
                    {String(item.empId)} - {item.fullName}
                  </Text>
                </Flex>
              </Flex>
            ))
          )}
        </Flex>
      </Col>

      {/* RIGHT PANEL - Employee Table */}
      <Col span={16}>
        <Flex vertical gap={8}>
          <Flex gap={8} align="center">
            <Input
              placeholder="Search by Emp ID, User Name, or Department"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ flex: 1 }}
            />
            <Button onClick={handleSelectAll}>
              {isAllSelected ? "Unselect All" : "Select All"}
            </Button>
          </Flex>
          <Table
            size="small"
            dataSource={filteredEmployeesData}
            columns={columns}
            rowSelection={rowSelection}
            rowKey="userId"
            pagination={{
              position: ["bottomCenter"],
              showSizeChanger: false,
            }}
            className={css`
              .ant-table-thead > tr > th {
                white-space: nowrap;
                text-align: center;
                background-color: transparent;
                height: 32px;
                color: ${isDefaultTheme ? "#230D39" : "white"};
                font-weight: 700 !important;
              }
              .ant-table-tbody > tr > td {
                background-color: transparent;
                text-align: center;
                height: 44px;
                color: ${isDefaultTheme ? "#230D39" : "white"};
              }
              .ant-table-tbody > tr > td:nth-child(2) {
                text-align: left;
              }
              .ant-table {
                background-color: transparent;
              }
              flex: 1;
            `}
          />
        </Flex>
      </Col>
    </Row>
  );
});

AssignEmployeesTab.displayName = "AssignEmployeesTab";

export default AssignEmployeesTab;
